import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaNewspaper, FaPlus, FaEdit, FaTrash, FaEye, FaStar, FaSearch,
  FaFilter, FaCheck, FaArchive, FaRedo, FaChartBar, FaTimes
} from 'react-icons/fa';
import noticiasMuralService from '../../services/noticiasMuralService';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Página de gerenciamento de notícias (Admin/Operador)
 */
const GerenciarNoticias = () => {
  const { hasPermission } = useAuth();
  
  const [noticias, setNoticias] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filtros
  const [statusFiltro, setStatusFiltro] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Paginação
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Modal de confirmação
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noticiaParaExcluir, setNoticiaParaExcluir] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Carregar categorias e estatísticas
  useEffect(() => {
    const carregarDados = async () => {
      try {
        const [catResponse, statsResponse] = await Promise.all([
          noticiasMuralService.listarCategorias(),
          noticiasMuralService.getEstatisticas()
        ]);
        
        if (catResponse.success) {
          setCategorias(catResponse.data);
        }
        if (statsResponse.success) {
          setEstatisticas(statsResponse.data);
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };
    carregarDados();
  }, []);

  // Carregar notícias
  const carregarNoticias = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit: 10,
        ...(statusFiltro && { status: statusFiltro }),
        ...(categoriaFiltro && { categoria_id: categoriaFiltro }),
        ...(searchTerm && { search: searchTerm }),
        orderBy: 'created_at',
        orderDirection: 'DESC'
      };

      const response = await noticiasMuralService.listarParaGerenciamento(params);
      
      if (response.success) {
        setNoticias(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotal(response.pagination.total);
      }
    } catch (error) {
      console.error('Erro ao carregar notícias:', error);
      setError('Erro ao carregar notícias');
    } finally {
      setLoading(false);
    }
  }, [page, statusFiltro, categoriaFiltro, searchTerm]);

  useEffect(() => {
    carregarNoticias();
  }, [carregarNoticias]);

  // Ações
  const handlePublicar = async (id) => {
    try {
      setActionLoading(true);
      const response = await noticiasMuralService.publicar(id);
      if (response.success) {
        carregarNoticias();
        // Recarregar estatísticas
        const statsResponse = await noticiasMuralService.getEstatisticas();
        if (statsResponse.success) setEstatisticas(statsResponse.data);
      }
    } catch (error) {
      console.error('Erro ao publicar:', error);
      alert('Erro ao publicar notícia');
    } finally {
      setActionLoading(false);
    }
  };

  const handleArquivar = async (id) => {
    try {
      setActionLoading(true);
      const response = await noticiasMuralService.arquivar(id);
      if (response.success) {
        carregarNoticias();
        const statsResponse = await noticiasMuralService.getEstatisticas();
        if (statsResponse.success) setEstatisticas(statsResponse.data);
      }
    } catch (error) {
      console.error('Erro ao arquivar:', error);
      alert('Erro ao arquivar notícia');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExcluir = async () => {
    if (!noticiaParaExcluir) return;
    
    try {
      setActionLoading(true);
      const response = await noticiasMuralService.excluir(noticiaParaExcluir.id);
      if (response.success) {
        setShowDeleteModal(false);
        setNoticiaParaExcluir(null);
        carregarNoticias();
        const statsResponse = await noticiasMuralService.getEstatisticas();
        if (statsResponse.success) setEstatisticas(statsResponse.data);
      }
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert('Erro ao excluir notícia');
    } finally {
      setActionLoading(false);
    }
  };

  // Formatar data
  const formatarData = (dataString) => {
    if (!dataString) return '-';
    return new Date(dataString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const config = {
      rascunho: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Rascunho' },
      publicado: { bg: 'bg-green-100', text: 'text-green-700', label: 'Publicado' },
      arquivado: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Arquivado' }
    };
    const { bg, text, label } = config[status] || config.rascunho;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
            <FaNewspaper className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gerenciar Notícias</h1>
            <p className="text-sm text-gray-500">Crie e gerencie as notícias do mural</p>
          </div>
        </div>

        {hasPermission('noticias.create') && (
          <Link
            to="/mural/gerenciar/nova"
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <FaPlus className="w-4 h-4" />
            Nova Notícia
          </Link>
        )}
      </div>

      {/* Estatísticas */}
      {estatisticas && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-gray-900">{estatisticas.total || 0}</div>
            <div className="text-sm text-gray-500">Total</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-green-600">{estatisticas.publicadas || 0}</div>
            <div className="text-sm text-gray-500">Publicadas</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-gray-600">{estatisticas.rascunhos || 0}</div>
            <div className="text-sm text-gray-500">Rascunhos</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-amber-600">{estatisticas.arquivadas || 0}</div>
            <div className="text-sm text-gray-500">Arquivadas</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-yellow-500">{estatisticas.destaques || 0}</div>
            <div className="text-sm text-gray-500">Destaques</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-blue-600">{estatisticas.total_visualizacoes || 0}</div>
            <div className="text-sm text-gray-500">Visualizações</div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          {/* Busca */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                placeholder="Buscar por título..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status */}
          <select
            value={statusFiltro}
            onChange={(e) => { setStatusFiltro(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Todos os status</option>
            <option value="rascunho">Rascunho</option>
            <option value="publicado">Publicado</option>
            <option value="arquivado">Arquivado</option>
          </select>

          {/* Categoria */}
          <select
            value={categoriaFiltro}
            onChange={(e) => { setCategoriaFiltro(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">Todas as categorias</option>
            {categorias.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.nome}</option>
            ))}
          </select>

          {/* Limpar */}
          {(statusFiltro || categoriaFiltro || searchTerm) && (
            <button
              onClick={() => { setStatusFiltro(''); setCategoriaFiltro(''); setSearchTerm(''); setPage(1); }}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
            >
              <FaTimes className="w-4 h-4" />
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-2 text-gray-500">Carregando...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            {error}
            <button onClick={carregarNoticias} className="block mx-auto mt-2 text-indigo-600 underline">
              Tentar novamente
            </button>
          </div>
        ) : noticias.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FaNewspaper className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Nenhuma notícia encontrada</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Destaque</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Views</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Criado em</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {noticias.map(noticia => (
                    <tr key={noticia.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 line-clamp-1">{noticia.titulo}</div>
                        {noticia.subtitulo && (
                          <div className="text-sm text-gray-500 line-clamp-1">{noticia.subtitulo}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                          style={{ 
                            backgroundColor: noticia.categoria_cor ? `${noticia.categoria_cor}20` : '#f3f4f6',
                            color: noticia.categoria_cor || '#6b7280'
                          }}
                        >
                          {noticia.categoria_nome || 'Sem categoria'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={noticia.status} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {noticia.destaque === 1 && (
                          <FaStar className="w-4 h-4 text-amber-500 mx-auto" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600">
                        {noticia.visualizacoes || 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatarData(noticia.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {/* Ver */}
                          <Link
                            to={`/mural/${noticia.id}`}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                            title="Visualizar"
                          >
                            <FaEye className="w-4 h-4" />
                          </Link>

                          {/* Editar */}
                          {hasPermission('noticias.update') && (
                            <Link
                              to={`/mural/gerenciar/${noticia.id}/editar`}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Editar"
                            >
                              <FaEdit className="w-4 h-4" />
                            </Link>
                          )}

                          {/* Publicar (se rascunho) */}
                          {hasPermission('noticias.publish') && noticia.status === 'rascunho' && (
                            <button
                              onClick={() => handlePublicar(noticia.id)}
                              disabled={actionLoading}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50"
                              title="Publicar"
                            >
                              <FaCheck className="w-4 h-4" />
                            </button>
                          )}

                          {/* Arquivar (se publicado) */}
                          {hasPermission('noticias.publish') && noticia.status === 'publicado' && (
                            <button
                              onClick={() => handleArquivar(noticia.id)}
                              disabled={actionLoading}
                              className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg disabled:opacity-50"
                              title="Arquivar"
                            >
                              <FaArchive className="w-4 h-4" />
                            </button>
                          )}

                          {/* Excluir */}
                          {hasPermission('noticias.delete') && (
                            <button
                              onClick={() => { setNoticiaParaExcluir(noticia); setShowDeleteModal(true); }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Excluir"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {noticias.length} de {total} notícias
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-600">
                    Página {page} de {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmar Exclusão</h3>
            <p className="text-gray-600 mb-4">
              Tem certeza que deseja excluir a notícia "{noticiaParaExcluir?.titulo}"?
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setNoticiaParaExcluir(null); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleExcluir}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciarNoticias;
